/**
 * ArcEcosystemProvider — centralised, READ-ONLY ARC ecosystem context.
 *
 * This is the single source of truth that every Arena UI surface should read
 * from when it needs to know:
 *   • is a wallet connected?
 *   • what's the on-chain ARC balance?
 *   • what tier does the user qualify for?
 *   • what fee discount applies?
 *   • is feature X (VIP / creator / arc-only) accessible?
 *   • is the wallet on the right network?
 *   • is the contract live or pre-launch?
 *
 * STRICT V1 RULES enforced by this layer:
 *   - read-only RPC calls only (eth_call to balanceOf)
 *   - NO eth_signTypedData / eth_sendTransaction / wallet_requestPermissions
 *   - NO custodial storage of balances; balance is a derived display value
 *   - NO blockchain dependency in the gameplay hot path; this provider lives
 *     in the marketing/dashboard surface, not inside game-server runtime
 *
 * Hooks exposed:
 *   useArcEcosystem()    – full context object
 *   useArcWallet()       – { wallet, connect, disconnect }
 *   useArcContract()     – { address, chain_id, is_live, decimals, ... }
 *   useArcBalance()      – { balance, rawBalance, refresh, lastSync, loading }
 *   useArcStatus()       – { connected, correctNetwork, isPreLaunch, wrongNetwork, error }
 *   useArcTier()         – { current, next, progress, index, tiers }
 *   useArcFeeDiscount()  – number (0..100) — % discount on platform commission
 *   useArcEligibility(min_arc) – { eligible, shortfall }
 *   useArcAccess(feature)      – boolean — vip | creator | arc_only | fee_discount
 */

import {
  createContext, useContext, useEffect, useMemo, useState, useCallback, useRef,
} from "react";
import { toast } from "sonner";
import {
  connectMetaMask, getCurrentChainId, onAccountsChanged, onChainChanged, switchChain,
} from "@/lib/wallet";
import { readArcBalance, formatArcBalance, tierFromBalance } from "@/lib/arcContract";
import {
  getUtilityContract, getUtilityTiers, postWalletConnect, postAttestBalance,
} from "@/lib/api";

const REFRESH_MS = 15000;
const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

// Sprint 19 — canonical pre-launch message; single source of truth used
// across every connect surface so the wording cannot drift.
export const ARC_PRE_LAUNCH_MESSAGE =
  "ARC contract pending deployment — live balances will appear automatically after mainnet launch.";

/**
 * Bugfix — wallet-connect error stringifier.
 *
 * Provider errors come in three shapes:
 *   1. `Error` instances (our own throws + most fetch failures)
 *   2. Plain objects with `{ code, message }` (EIP-1193 / MetaMask)
 *   3. Strings or null
 *
 * This helper coerces every shape into a readable, user-safe message and
 * — importantly — maps known EIP-1193 codes to friendly copy so a user
 * rejection looks like "Wallet connection cancelled." instead of the
 * raw provider payload.
 */
function describeWalletError(err) {
  if (err == null) return "Wallet connection failed.";
  if (typeof err === "string") return err || "Wallet connection failed.";

  // EIP-1193 standard error codes
  const code = typeof err === "object" ? err.code : undefined;
  if (code === 4001) return "Wallet connection cancelled.";
  if (code === -32002) return "Wallet is already processing a request. Open MetaMask to continue.";
  if (code === 4902) return "BNB Smart Chain is not added to MetaMask yet.";
  // -32603 covers "internal" provider failures: wallet locked, extension
  // tab not focused, MetaMask Snap crashed, or a non-MetaMask provider
  // proxy that rejected the request internally. Tell the user the actual
  // remediation instead of a generic "internal error".
  if (code === -32603) {
    return "Wallet locked or unavailable. Open your wallet extension, unlock it, then try again.";
  }
  // -32601 (method not supported) and -32000..-32099 (server error band)
  // — surface a readable cousin of the -32603 message.
  if (code === -32601) {
    return "Wallet does not support that action. Switch to MetaMask or open the page in a wallet browser.";
  }

  if (err instanceof Error && err.message) return err.message;
  if (typeof err === "object" && typeof err.message === "string" && err.message) {
    return err.message;
  }
  if (typeof err === "object" && typeof err.reason === "string" && err.reason) {
    return err.reason;
  }
  return "Wallet connection failed.";
}

/**
 * Click-handler wrapper used by every wallet `Connect` button across the
 * app (Header, Hero, Presale, TournamentRail, HoldingsWidget,
 * ArcDiscountBadge, WalletCard).
 *
 * Guarantees:
 *   1. The promise returned to React is ALWAYS resolved (never rejected).
 *   2. Any thrown value is coerced to a friendly string via
 *      `describeWalletError` and surfaced via `toast.error`.
 *   3. The React runtime overlay cannot fire from a wallet-connect
 *      failure — even if a future regression rethrows.
 *
 * Usage:
 *   <button onClick={() => safeWalletClick(connect)} ...>
 */
export function safeWalletClick(fn) {
  return (async () => {
    try {
      return await fn();
    } catch (err) {
      const msg = describeWalletError(err);
      try { toast.error(msg); } catch (_) { /* toast missing? still safe */ }
      // eslint-disable-next-line no-console
      console.warn("[wallet] click handler caught:", msg);
      return null;
    }
  })();
}

const ArcEcosystemContext = createContext(null);

export function ArcEcosystemProvider({ children }) {
  // Static config (loaded once)
  const [contract, setContract] = useState(null);
  const [tiers, setTiers] = useState([]);

  // Live state
  const [wallet, setWallet] = useState(null);   // { address, chainId }
  const [chainId, setChainId] = useState(null); // separate so chainChanged works without disconnect
  const [rawBalance, setRawBalance] = useState(0n);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sprint 19 connect-unification: shared connection lifecycle.
  // `connecting` is true while a single in-flight connect() is being awaited.
  // `connectError` mirrors the most recent failure so buttons + badges can
  // surface inline error state in addition to the existing toast.
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState(null);
  const connectInFlightRef = useRef(null);

  // Sprint 19 micro-polish — display-only "Connected since HH:MM" timestamp.
  // Initialized ONLY after a successful connect() resolves. Cleared on
  // disconnect() and when MetaMask's accountsChanged drops the account to
  // null. Lives in runtime React state only — resets on page reload by
  // design. No persistence, no polling, no relative ticking.
  const [connectedAt, setConnectedAt] = useState(null);

  const refreshTimer = useRef(null);

  // ---- Load static config ----
  useEffect(() => {
    Promise.all([getUtilityContract(), getUtilityTiers()])
      .then(([c, t]) => { setContract(c); setTiers(t.tiers || []); })
      .catch(() => setError("Failed to load ARC ecosystem configuration"));
  }, []);

  // ---- Wallet event subscriptions ----
  useEffect(() => {
    const offAcc = onAccountsChanged((addr) => {
      if (!addr) {
        setWallet(null);
        setRawBalance(0n);
        setConnectedAt(null);
        return;
      }
      setWallet((w) => ({ ...(w || {}), address: addr }));
    });
    const offChain = onChainChanged((id) => setChainId(id));
    return () => { offAcc?.(); offChain?.(); };
  }, []);

  // ---- Derived flags ----
  const isPreLaunch = contract?.is_live === false;
  const correctNetwork = chainId != null && contract?.chain_id != null && chainId === contract.chain_id;
  const connected = !!wallet?.address;

  const balance = useMemo(
    () => formatArcBalance(rawBalance, contract?.decimals || 18),
    [rawBalance, contract?.decimals]
  );

  const tier = useMemo(() => tierFromBalance(balance, tiers), [balance, tiers]);

  // Per-feature access map keyed by current tier
  const access = useMemo(() => {
    if (!tier?.current) return { vip: false, creator: false, arc_only: false, fee_discount: false };
    const matched = tiers.find((t) => t.id === tier.current.id);
    return matched?.features || { vip: false, creator: false, arc_only: false, fee_discount: false };
  }, [tier?.current?.id, tiers]);

  const feeDiscountPct = useMemo(() => {
    if (!tier?.current) return 0;
    const matched = tiers.find((t) => t.id === tier.current.id);
    return matched?.fee_discount_pct ?? 0;
  }, [tier?.current?.id, tiers]);

  // Sprint 7: surface backend feature flag so the badge & hook can switch
  // from cosmetic 'active' to live 'active-live'.
  const feeDiscountEnabled = !!contract?.fee_discount_enabled;

  // ---- Balance refresh (only when safe) ----
  const refresh = useCallback(async () => {
    if (!connected || !contract?.address) return;
    if (isPreLaunch || contract.address.toLowerCase() === ZERO_ADDRESS) {
      setRawBalance(0n); return;
    }
    if (!correctNetwork) {
      setError("Wrong network — switch to BNB Smart Chain");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const raw = await readArcBalance({ contractAddress: contract.address, wallet: wallet.address });
      setRawBalance(raw);
      setLastSync(new Date());

      // V1.2 — write the freshly-read balance into the backend attestation
      // cache so the gameplay/fee-quote path can use it without ever calling
      // RPC itself. Best-effort: failures don't break the read flow.
      try {
        const human = formatArcBalance(raw, contract.decimals || 18);
        await postAttestBalance({
          wallet_address: wallet.address,
          balance_arc: human,
          source: "frontend",
        });
      } catch (_) { /* attestation is non-critical */ }
    } catch (err) {
      setError(err.message || "Could not read on-chain balance");
    } finally {
      setLoading(false);
    }
  }, [connected, contract, correctNetwork, isPreLaunch, wallet?.address]);

  // Auto-refresh loop
  useEffect(() => {
    if (refreshTimer.current) { clearInterval(refreshTimer.current); refreshTimer.current = null; }
    if (!connected || isPreLaunch || !correctNetwork) {
      setRawBalance(0n);
      return;
    }
    refresh();
    refreshTimer.current = setInterval(refresh, REFRESH_MS);
    return () => { if (refreshTimer.current) clearInterval(refreshTimer.current); };
  }, [connected, isPreLaunch, correctNetwork, refresh]);

  // ---- Actions ----
  const connect = useCallback(async () => {
    // Sprint 19: de-duplicate concurrent connect() calls so multiple buttons
    // (Header + Hero + Holdings + Discount badge + Tournament card) clicked
    // in any order resolve to the **same** in-flight MetaMask flow rather
    // than firing duplicate popups.
    if (connectInFlightRef.current) {
      return connectInFlightRef.current;
    }

    // Already-connected guard: if we have a wallet on the correct network
    // (or pre-launch state where any connection is fine), treat this as a
    // no-op so re-clicks don't re-prompt MetaMask.
    if (wallet?.address) {
      return wallet;
    }

    setConnecting(true);
    setConnectError(null);

    const promise = (async () => {
      try {
        const w = await connectMetaMask();
        setWallet(w);
        setChainId(w.chainId);
        setConnectedAt(new Date());
        toast.success(`Wallet connected: ${w.address.slice(0, 6)}…${w.address.slice(-4)}`);
        try { await postWalletConnect({ wallet_address: w.address, chain_id: w.chainId }); } catch (_) {}
        return w;
      } catch (err) {
        // Bugfix — never rethrow the raw provider error out of the click
        // handler. We surface the failure via state + toast and return
        // null so callers (Header, PresaleWidget, etc.) can treat the
        // click as a no-op without producing an unhandled promise
        // rejection (which previously rendered as "[object Object]" in
        // the React runtime overlay).
        const msg = describeWalletError(err);
        setConnectError(msg);
        toast.error(msg);
        return null;
      } finally {
        setConnecting(false);
        connectInFlightRef.current = null;
      }
    })();

    connectInFlightRef.current = promise;
    return promise;
  }, [wallet?.address]);

  const disconnect = useCallback(() => {
    setWallet(null);
    setRawBalance(0n);
    setLastSync(null);
    setConnectError(null);
    setConnectedAt(null);
  }, []);

  const switchToBSC = useCallback(async () => {
    if (!contract) return null;
    try {
      await switchChain({
        chainId: contract.chain_id,
        chainIdHex: contract.chain_id_hex,
        chainName: contract.network_name,
      });
      toast.success("Switched to BNB Smart Chain");
      return true;
    } catch (err) {
      // Bugfix — same defensive contract as connect(): never rethrow.
      toast.error(describeWalletError(err));
      return null;
    }
  }, [contract]);

  // ---- Public surface ----
  const value = useMemo(() => ({
    // identity
    wallet,
    contract,
    tiers,
    // balances
    balance,
    rawBalance,
    // status
    status: {
      connected,
      correctNetwork,
      isPreLaunch,
      wrongNetwork: connected && !isPreLaunch && !correctNetwork,
      loading,
      error,
      lastSync,
      connecting,
      connectError,
      connectedAt,
      preLaunchMessage: ARC_PRE_LAUNCH_MESSAGE,
    },
    // derived
    tier,
    access,
    feeDiscountPct,
    feeDiscountEnabled,
    // actions
    actions: { connect, disconnect, refresh, switchToBSC },
  }), [
    wallet, contract, tiers, balance, rawBalance,
    connected, correctNetwork, isPreLaunch, loading, error, lastSync,
    connecting, connectError, connectedAt,
    tier, access, feeDiscountPct, feeDiscountEnabled,
    connect, disconnect, refresh, switchToBSC,
  ]);

  return (
    <ArcEcosystemContext.Provider value={value}>
      {children}
    </ArcEcosystemContext.Provider>
  );
}

// =====================  HOOKS  =====================

export function useArcEcosystem() {
  const ctx = useContext(ArcEcosystemContext);
  if (!ctx) throw new Error("useArcEcosystem must be used inside <ArcEcosystemProvider>");
  return ctx;
}

export function useArcWallet() {
  const { wallet, actions } = useArcEcosystem();
  return { wallet, connect: actions.connect, disconnect: actions.disconnect };
}

/**
 * Sprint 19 — canonical connect hook. Every Connect surface across the app
 * (Header, Hero, HoldingsWidget, ArcDiscountBadge, TournamentRail, PresaleWidget)
 * resolves through this single hook. The shape returned is the **complete
 * lifecycle state** of the wallet connection — disconnected, connecting,
 * connected, wrong-network, pre-launch, connection-failed — so every button
 * can render consistently without re-deriving status from raw fields.
 *
 * Read-only + connect-only by design. No tx, approval, signature, or permit
 * exposed here. Pre-launch posture is preserved via `isPreLaunch` +
 * `preLaunchMessage`.
 */
export function useArcConnect() {
  const { wallet, status, actions } = useArcEcosystem();
  const connected = !!wallet?.address;

  // UI state derivation — exactly one is true at any moment.
  let uiState = "disconnected";
  if (status.connecting) uiState = "connecting";
  else if (status.connectError && !connected) uiState = "connection-failed";
  else if (connected && status.wrongNetwork) uiState = "wrong-network";
  else if (connected && status.isPreLaunch) uiState = "pre-launch";
  else if (connected) uiState = "connected";

  return {
    // identity
    wallet,
    address: wallet?.address || null,
    // single in-flight connection lifecycle
    connect: actions.connect,
    disconnect: actions.disconnect,
    connecting: !!status.connecting,
    connectError: status.connectError || null,
    // canonical UI state — components map this to label/disabled/style
    uiState,
    // derived flags (mirrored from status; here for convenience)
    isConnected: connected,
    isWrongNetwork: !!status.wrongNetwork,
    isPreLaunch: !!status.isPreLaunch,
    // canonical pre-launch wording (single source of truth)
    preLaunchMessage: status.preLaunchMessage,
    // network helper for wrong-network surfaces
    switchToBSC: actions.switchToBSC,
    // Sprint 19 micro-polish — display-only "connected since" timestamp
    // (Date | null). Runtime state only; cleared on disconnect/reload.
    connectedAt: status.connectedAt || null,
  };
}

export function useArcContract() {
  return useArcEcosystem().contract;
}

export function useArcBalance() {
  const { balance, rawBalance, status, actions } = useArcEcosystem();
  return {
    balance,
    rawBalance,
    refresh: actions.refresh,
    lastSync: status.lastSync,
    loading: status.loading,
  };
}

export function useArcStatus() {
  const { status, actions } = useArcEcosystem();
  return { ...status, switchToBSC: actions.switchToBSC };
}

export function useArcTier() {
  const { tier, tiers } = useArcEcosystem();
  return { ...tier, tiers };
}

export function useArcFeeDiscount() {
  return useArcEcosystem().feeDiscountPct;
}

/**
 * V1.2 — true when the platform feature flag has flipped the discount from
 * cosmetic to live. UI surfaces should swap "future discount" copy for
 * "Discount Applied" copy when this returns true.
 */
export function useArcFeeDiscountActive() {
  const { feeDiscountPct, feeDiscountEnabled, status } = useArcEcosystem();
  return {
    pct: feeDiscountPct,
    isActive: !!feeDiscountEnabled && feeDiscountPct > 0 && status.connected
              && !status.wrongNetwork && !status.isPreLaunch,
    isEnabledGlobally: !!feeDiscountEnabled,
  };
}

/**
 * Boolean eligibility helper — feeds Holdings widget, Tournament cards, future
 * VIP gates and creator gates. Returns shortfall in human ARC for "X more to
 * unlock" messaging.
 */
export function useArcEligibility(min_arc = 0) {
  const { balance, status } = useArcEcosystem();
  if (!status.connected) return { eligible: false, shortfall: min_arc, reason: "connect_required" };
  if (status.wrongNetwork)  return { eligible: false, shortfall: min_arc, reason: "wrong_network" };
  if (status.isPreLaunch)   return { eligible: false, shortfall: min_arc, reason: "pre_launch" };
  if (balance >= min_arc)   return { eligible: true,  shortfall: 0,        reason: "unlocked" };
  return { eligible: false, shortfall: Math.max(0, min_arc - balance), reason: "locked" };
}

/**
 * Per-feature access — string keys: "vip" | "creator" | "arc_only" | "fee_discount".
 * Resolves via the user's current tier features map (off-chain, declarative).
 */
export function useArcAccess(feature) {
  const { access, status } = useArcEcosystem();
  if (!status.connected || status.wrongNetwork || status.isPreLaunch) return false;
  return !!access?.[feature];
}
